
import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { 
  User as UserIcon, CheckCircle2, AlertCircle, Save, Linkedin, 
  Sun, Moon, X, Pencil, MapPin, GraduationCap,
  Crown, Mail, Map as MapIcon,
  ShieldCheck, ShieldAlert, RotateCw, FileText, Clock, XCircle, Activity, Lock
} from 'lucide-react';
import { 
  saveUserProfile, 
  subscribeToUserProfile,
  subscribeToActiveChats,
  subscribeToDirectory
} from '../services/db';
import { verifyProfessionalIdentity } from '../services/geminiService';
import { UserProfile, UserRole, CollaborationStatus, PeerChat, ViewState } from '../types';
import TermsModal from './TermsModal';
import { TERMS_VERSION } from '../constants';
import { COUNTRIES } from '../utils/regionalAccess';

interface ProfileScreenProps {
  user: User | null;
  isLocked?: boolean;
  onLogout?: () => void;
  onNavigate?: (view: ViewState) => void;
  initialProfile?: UserProfile | null;
}

const SPECIALTIES_LIST = [
  "Radiología General", "Neurorradiología", "Neurorravascular intervencionista", "Cabeza- Cuello", 
  "Musculoesquelética", "Tórax", "Abdomen", "Cardiaca", "Vascular intervencionista", 
  "Mama", "Nuclear/PET", "Ecografía General", "Ecografía MSK", "Ecografía Intervencionista"
];

const EXP_OPTIONS = [
  "menos de 5 años", "5 a 10", "10 a 20", "20 a 30", "más de 30 años"
];

const ProfileScreen: React.FC<ProfileScreenProps> = ({ user, isLocked: initialIsLocked = false, onLogout, onNavigate, initialProfile = null }) => {
  const [isEditing, setIsEditing] = useState(!!initialIsLocked);
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('module_theme_profile');
    if (saved) return saved === 'dark';
    return localStorage.getItem('app_theme') === 'dark';
  });

  const [originalProfile, setOriginalProfile] = useState<UserProfile | null>(initialProfile);
  const [activeChats, setActiveChats] = useState<PeerChat[]>([]);
  const [directory, setAllUsers] = useState<UserProfile[]>([]);
  const [formData, setFormData] = useState({
    name: initialProfile?.displayName || user?.displayName || '',
    email: initialProfile?.email || user?.email || '',
    dni: initialProfile?.dni || '',
    country: initialProfile?.country || 'España',
    college: initialProfile?.college || '',
    collegeNumber: initialProfile?.collegeNumber || '',
    linkedin: initialProfile?.linkedin || '',
    mainSpecialty: initialProfile?.mainSpecialty || '',
    workplace: initialProfile?.workplace || '',
    workCity: initialProfile?.workCity || '',
    workCountry: initialProfile?.workCountry || initialProfile?.country || 'España',
    teleRadiologyInterest: initialProfile?.teleRadiologyInterest || false,
    experienceYears: (initialProfile?.experienceYears as any) || 'menos de 5 años',
    residentYear: (initialProfile?.residentYear as number) || 1,
    otherMedicalSpecialty: initialProfile?.otherMedicalSpecialty || ''
  });

  const [location, setLocation] = useState<{lat: number, lng: number} | null>(initialProfile?.location || null);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!initialProfile && user?.uid !== 'guest_dev_offline' && user?.uid !== 'anonymous');
  const [locationSuccess, setLocationSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]); 
  const [collabStatus, setCollabStatus] = useState<CollaborationStatus>('AVAILABLE');
  const [role, setRole] = useState<UserRole>('RADIOLOGIST');
  
  useEffect(() => {
    if (!user) return;
    const unsubChats = subscribeToActiveChats(user.uid, setActiveChats);
    const unsubDir = subscribeToDirectory(user.uid, (users) => setAllUsers(users));
    return () => { unsubChats(); unsubDir(); };
  }, [user]);

  const handleOpenChat = (chat: PeerChat) => {
    if (!user) return;
    const otherId = chat.participants.find(p => p !== user.uid);
    if (otherId) {
      localStorage.setItem('modular_open_chat_user_id', otherId);
      onNavigate?.(ViewState.COMMUNITY);
    }
  };

  const isFieldDisabled = !isEditing;
  const isLocationLocked = !!(originalProfile?.isVerified || initialProfile?.isVerified);

  useEffect(() => {
    if (!isInitialLoading) return;
    const timer = setTimeout(() => {
      console.warn("Profile loading timeout reached. Forcing loading end.");
      setIsInitialLoading(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [isInitialLoading]);

  useEffect(() => {
    if (initialProfile) {
      setFormData(prev => ({
        ...prev,
        name: initialProfile.displayName || prev.name,
        email: initialProfile.email || prev.email,
        dni: initialProfile.dni || prev.dni,
        country: initialProfile.country || prev.country,
        college: initialProfile.college || prev.college,
        collegeNumber: initialProfile.collegeNumber || prev.collegeNumber,
        linkedin: initialProfile.linkedin || prev.linkedin,
        mainSpecialty: initialProfile.mainSpecialty || prev.mainSpecialty,
        workplace: initialProfile.workplace || prev.workplace,
        workCity: initialProfile.workCity || prev.workCity,
        workCountry: initialProfile.workCountry || initialProfile.country || prev.workCountry,
        teleRadiologyInterest: initialProfile.teleRadiologyInterest ?? prev.teleRadiologyInterest,
        experienceYears: (initialProfile.experienceYears as any) || prev.experienceYears,
        residentYear: (initialProfile.residentYear as number) || prev.residentYear,
        otherMedicalSpecialty: initialProfile.otherMedicalSpecialty || prev.otherMedicalSpecialty
      }));
      setOriginalProfile(initialProfile);
      setRole(initialProfile.role as UserRole || 'RADIOLOGIST');
      setCollabStatus(initialProfile.status as CollaborationStatus || 'AVAILABLE');
      if (initialProfile.location) setLocation(initialProfile.location);
      setIsInitialLoading(false);
    }
  }, [initialProfile]);

  useEffect(() => {
    const handleThemeChange = () => {
       const saved = localStorage.getItem('module_theme_profile');
       if (!saved) setIsDarkMode(localStorage.getItem('app_theme') === 'dark');
    };
    window.addEventListener('app_theme_changed', handleThemeChange);
    return () => window.removeEventListener('app_theme_changed', handleThemeChange);
  }, []);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('module_theme_profile', next ? 'dark' : 'day');
    window.dispatchEvent(new Event('module_theme_changed'));
  };

  useEffect(() => {
    if (!user) return;
    
    const unsub = subscribeToUserProfile(user.uid, (profile) => {
       setIsInitialLoading(false);
       if (profile) {
          setOriginalProfile(profile);
          
          if (!isEditing && !isSaving) {
            setFormData({
               name: profile.displayName || user.displayName || '',
               email: profile.email || user.email || '',
               dni: profile.dni || '',
               country: profile.country || 'España',
               college: profile.college || '',
               collegeNumber: profile.collegeNumber || '',
               linkedin: profile.linkedin || '',
               mainSpecialty: profile.mainSpecialty || '',
               workplace: profile.workplace || '',
               workCity: profile.workCity || '',
               workCountry: profile.workCountry || profile.country || 'España',
               teleRadiologyInterest: profile.teleRadiologyInterest || false,
               experienceYears: profile.experienceYears || 'menos de 5 años',
               residentYear: profile.residentYear || 1,
               otherMedicalSpecialty: profile.otherMedicalSpecialty || ''
            });

            const savedSpecialties = Array.isArray(profile.specialties) ? profile.specialties : [];
            const validSelected = savedSpecialties.filter(s => 
              SPECIALTIES_LIST.includes(s) && s !== profile.mainSpecialty
            );
            setSelectedSpecialties(Array.from(new Set(validSelected)).slice(0, 3));
            
            setRole(profile.role || 'RADIOLOGIST');
            setCollabStatus(profile.status || 'AVAILABLE');
            setLocation(profile.location || null);
            if (profile.location) setLocationSuccess(true);
            if (profile.termsAcceptedAt) setTermsAccepted(true);
          }
       }
    });
    return () => unsub();
  }, [user, isEditing, isSaving]);

  const handleAcceptTerms = async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      const updatedProfile = {
        ...originalProfile,
        uid: user.uid,
        displayName: formData.name || user.displayName || '',
        email: formData.email || user.email || '',
        termsAcceptedAt: Date.now(),
        termsVersion: TERMS_VERSION,
        updatedAt: Date.now()
      };
      await saveUserProfile(user.uid, updatedProfile as UserProfile);
      setTermsAccepted(true);
      setShowTermsModal(false);
    } catch (error) {
      console.error("Error al aceptar términos:", error);
      alert("No se ha podido registrar su aceptación de términos en este momento. Por favor, reintente.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearchWorkplace = async () => {
    if (!formData.workplace.trim() && !formData.workCity.trim()) return;
    setIsSearchingLocation(true);
    setLocationSuccess(false);

    try {
      const query = encodeURIComponent(`${formData.workplace}, ${formData.workCity}, ${formData.workCountry}`);
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`, {
        headers: { 'Accept-Language': 'es' }
      });
      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        setLocation({ lat: parseFloat(lat), lng: parseFloat(lon) });
        setLocationSuccess(true);
      } else {
        alert("No se ha podido localizar la dirección del centro médico. Por favor, verifique los datos ingresados.");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearchingLocation(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    if (name === 'mainSpecialty') {
       setSelectedSpecialties(prev => prev.filter(s => s !== value));
    }
    
    setFormData(prev => ({ ...prev, [name]: val }));
    if (name === 'workplace' || name === 'workCity') {
      setLocationSuccess(false);
    }
  };

  const toggleSpecialty = (spec: string) => {
    if (isFieldDisabled) return; 
    if (spec === formData.mainSpecialty) return; 

    setSelectedSpecialties(prev => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const exists = list.includes(spec);
        if (exists) return list.filter(s => s !== spec);
        if (list.length >= 3) return list; 
        return [...list, spec];
    });
  };

  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);
  const [highlightMissing, setHighlightMissing] = useState(false);

  const handleSave = async () => {
    if (!user) return;
    
    const incomplete = isProfileIncomplete();
    
    if (!termsAccepted) {
      alert("Para continuar con su registro profesional, es necesario aceptar los términos y condiciones de uso.");
      return;
    }

    if (incomplete && !showIncompleteWarning) {
      setHighlightMissing(true);
      setShowIncompleteWarning(true);
      return;
    }

    setIsSaving(true); 
    setHighlightMissing(false);
    setShowIncompleteWarning(false);

    try {
      // isVerified NUNCA se toca aquí: es un campo exclusivo del Master (validación final).
      // El grounding de la IA solo produce una PRE-aprobación informativa para el panel de gestión.
      let verifiedStatus = originalProfile?.isVerified || false;
      let aiPreApproved = originalProfile?.aiPreApproved;
      let validationReport = originalProfile?.validationReport || "";
      let aiConfidenceScore = originalProfile?.aiConfidenceScore;
      let lastAuditedAt = originalProfile?.lastAuditedAt;
      const linkedinChanged = originalProfile?.linkedin !== formData.linkedin;
      const nameChanged = originalProfile?.displayName !== formData.name;
      const collegeChanged = originalProfile?.college !== formData.college;

      if (user.uid === 'guest_dev_offline' || 
          user.email?.toLowerCase() === 'dr.francisco.reyes@gmail.com') {
         // El Master no necesita auditoría de IA ni validación manual de sí mismo.
         verifiedStatus = true;
         aiPreApproved = true;
         validationReport = "Usuario Master: Validación automática omitida.";
         aiConfidenceScore = 100;
         lastAuditedAt = Date.now();
      } else if (!aiPreApproved || linkedinChanged || nameChanged || collegeChanged) {
         // Pre-validación automática de la IA (grounding). Solo informa al Master, no verifica.
         if (formData.name.length > 5 && (formData.linkedin.includes('linkedin.com') || formData.college.length > 3)) {
            try {
               const validation = await verifyProfessionalIdentity({
                  displayName: formData.name,
                  linkedin: formData.linkedin,
                  college: formData.college,
                  collegeNumber: formData.collegeNumber,
                  dni: formData.dni,
                  workplace: formData.workplace,
                  mainSpecialty: role === 'RADIOLOGIST' ? formData.mainSpecialty : '',
                  specialties: role === 'RADIOLOGIST' ? selectedSpecialties : [],
                  workCity: formData.workCity,
                  workCountry: formData.workCountry || formData.country,
                  role: role,
                  residentYear: formData.residentYear,
                  otherMedicalSpecialty: formData.otherMedicalSpecialty
               });
               aiPreApproved = validation.isValid;
               validationReport = validation.reason;
               aiConfidenceScore = validation.confidenceScore;
               lastAuditedAt = Date.now();
            } catch (geminiError) {
               console.warn("Fallo validación IA:", geminiError);
               validationReport = "Error técnico durante la validación automática.";
            }
         }
      }

      const finalAdditionalSpecialties = selectedSpecialties
        .filter(s => s !== formData.mainSpecialty)
        .slice(0, 3);

      const profileData: any = {
        uid: user.uid, 
        displayName: formData.name, 
        email: formData.email, 
        mainSpecialty: role === 'RADIOLOGIST' ? formData.mainSpecialty : '',
        specialties: role === 'RADIOLOGIST' ? finalAdditionalSpecialties : [],
        role: role, 
        status: collabStatus, 
        collegeNumber: formData.collegeNumber, 
        country: formData.country,
        dni: formData.dni, 
        college: formData.college, 
        linkedin: formData.linkedin, 
        workplace: formData.workplace,
        workCity: formData.workCity,
        workCountry: formData.workCountry, 
        teleRadiologyInterest: formData.teleRadiologyInterest,
        photoURL: user.photoURL || null, 
        updatedAt: Date.now(), 
        isVerified: verifiedStatus,
        aiPreApproved: aiPreApproved,
        aiConfidenceScore: aiConfidenceScore,
        lastAuditedAt: lastAuditedAt,
        validationReport: validationReport,
        termsAcceptedAt: termsAccepted ? (originalProfile?.termsAcceptedAt || Date.now()) : null,
        termsVersion: termsAccepted ? TERMS_VERSION : null
      };

      if (role === 'RADIOLOGIST') {
          profileData.experienceYears = formData.experienceYears;
          profileData.otherMedicalSpecialty = formData.otherMedicalSpecialty || "";
      } else {
          profileData.residentYear = Number(formData.residentYear);
      }

      if (location) profileData.location = location;

      await saveUserProfile(profileData as UserProfile); 
      
      // RESETEAR CONTADORES DE INGRESO INCOMPLETO
      localStorage.removeItem(`radia_incomplete_count_${user.uid}`);
      
      setIsEditing(false);

      if (initialIsLocked) {
        alert("Su registro profesional se ha completado con éxito.");
        setTimeout(() => window.location.reload(), 500);
      } else {
        alert("Su perfil profesional ha sido actualizado correctamente.");
      }
    } catch (e: any) { 
      console.error('Error guardando perfil:', e);
      alert("No se ha podido actualizar su perfil profesional. Por favor, verifique su conexión a la red.");
    } finally {
      setIsSaving(false);
    }
  };

  const getRankTitle = () => {
    const isMasterUser = user?.email?.toLowerCase() === 'dr.francisco.reyes@gmail.com' || 
                         user?.uid === 'guest_dev_offline';
    if (isMasterUser) return 'RADIOLOGY MASTER';
    if (role === 'RESIDENT') return `Residente R${formData.residentYear}`;
    const years = formData.experienceYears;
    const hasOtherSpec = (formData.otherMedicalSpecialty || "").trim() !== "";
    if (years === 'más de 30 años') return 'Maestro de la Radiología';
    if (years === '20 a 30') return 'Consultor en Jefe';
    if (years === '10 a 20') return 'Radiólogo Experto';
    if (years === '5 a 10' || (years === 'menos de 5 años' && hasOtherSpec)) return 'Radiólogo Senior';
    return 'Médico Radiólogo';
  };

  const isProfileIncomplete = () => {
    const essential = formData.name.trim() !== '' &&
                      formData.dni.trim() !== '' &&
                      formData.college.trim() !== '' &&
                      formData.collegeNumber.trim() !== '' &&
                      formData.workplace.trim() !== '' &&
                      formData.workCity.trim() !== '';

    if (role === 'RESIDENT') return !essential;
    return !essential || formData.mainSpecialty === '' || selectedSpecialties.length < 1;
  };

  const rank = getRankTitle();
  const isProfileIncompleteVal = isProfileIncomplete();
  
  const getInputClass = (value: string, isMandatory: boolean) => {
    const isEmpty = !value || value.trim() === '';
    const shouldHighlight = isMandatory && isEmpty && isProfileIncompleteVal;
    
    return `w-full px-3 py-2 border rounded-lg text-sm font-bold transition-all ${
      shouldHighlight 
        ? 'border-red-500 ring-2 ring-red-500/20 bg-red-50/10' 
        : (isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900')
    }`;
  };
  const isMaster = rank === 'RADIOLOGY MASTER' || rank === 'Maestro de la Radiología';
  const isVerified = originalProfile?.isVerified || false;

  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#1e2024] text-white font-sans">
        <div className="relative w-20 h-20 mb-8 flex items-center justify-center">
          <div className="absolute inset-0 rounded-2xl bg-blue-600 animate-pulse opacity-20"></div>
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div className="flex flex-col items-center space-y-2">
          <h2 className="text-sm font-black uppercase tracking-[0.4em] text-blue-500">MODULAR</h2>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 animate-pulse">
            Sincronizando Perfil Profesional...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full transition-colors ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'}`}>
      <div className={`px-5 py-4 border-b flex justify-between items-center sticky top-0 z-20 shadow-sm shrink-0 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
         <div className="flex items-center space-x-3 min-w-0">
            <div className={`${isEditing ? 'bg-blue-600' : 'bg-amber-500'} p-2 rounded-xl shadow-sm transition-colors shrink-0`}>
               {isEditing ? <Pencil size={18} className="text-white" /> : <UserIcon size={18} className="text-white" />}
            </div>
            <div className="min-w-0">
               <div className="flex items-center gap-1.5">
                  <h1 className={`font-black text-xl tracking-tight uppercase truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                     {isEditing ? 'Modificar Registro' : formData.name || 'Mi Perfil'}
                  </h1>
                  {isMaster && <Crown size={14} className="text-amber-500 shrink-0 mb-0.5" />}
                  {isVerified && <ShieldCheck size={14} className="text-emerald-500 shrink-0 mb-0.5" />}
               </div>
               {!isEditing && (
                  <div className="flex flex-col">
                     <p className={`text-[10px] font-black uppercase tracking-widest ${isMaster ? 'text-amber-500' : 'text-blue-500'}`}>{rank}</p>
                     {formData.workplace && (
                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tight truncate">
                           {formData.workplace} {formData.workCity && `• ${formData.workCity}`}
                        </p>
                     )}
                  </div>
               )}
            </div>
         </div>
         <div className="flex items-center space-x-2 shrink-0">
            <button onClick={toggleDarkMode} className={`p-2 rounded-lg border transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-yellow-400' : 'bg-white border-gray-200 text-gray-400'}`}>
               {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {initialIsLocked && onLogout && (
               <button onClick={onLogout} className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50/10 transition-colors">
                  <X size={20} />
               </button>
            )}
         </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 pb-24 custom-scrollbar">
        
        {/* MENSAJE DE AVISO SI ESTÁ INCOMPLETO */}
        {isProfileIncomplete() && (
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 p-4 rounded-2xl animate-in fade-in slide-in-from-top-2">
             <div className="flex items-start space-x-3 text-amber-700 dark:text-amber-400">
                <AlertCircle size={20} className="shrink-0 mt-0.5" />
                <div>
                   <p className="text-xs font-black uppercase tracking-tight">Perfil Incompleto</p>
                   <p className="text-[10px] font-medium leading-relaxed opacity-80 mt-0.5">
                      Para acceder a todas las funcionalidades del ecosistema y ser visible para colaboraciones, debes completar los campos marcados con (*).
                   </p>
                </div>
             </div>
          </div>
        )}

        {/* 0. DISPONIBILIDAD PARA COLABORACIÓN */}
        <div className={`rounded-xl shadow-sm border p-4 transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
           <label className="text-xs font-bold text-gray-400 uppercase mb-4 block tracking-wider border-b pb-2">0. Disponibilidad para Colaboración</label>
           <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { id: 'AVAILABLE', label: 'Disponible', color: 'bg-emerald-500', icon: CheckCircle2 },
                { id: 'READING', label: 'Informando', color: 'bg-blue-500', icon: FileText },
                { id: 'ON_CALL', label: 'De Guardia', color: 'bg-amber-500', icon: Clock },
                { id: 'NOT_AVAILABLE', label: 'No Disponible', color: 'bg-slate-400', icon: XCircle }
              ].map((status) => (
                <button
                  key={status.id}
                  onClick={() => isEditing && setCollabStatus(status.id as CollaborationStatus)}
                  disabled={!isEditing}
                  className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    collabStatus === status.id
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-transparent bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900'
                  } ${!isEditing ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <div className={`w-3 h-3 rounded-full ${status.color} shadow-sm`} />
                  <span className={`text-[10px] font-black uppercase tracking-tight ${collabStatus === status.id ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`}>
                    {status.label}
                  </span>
                </button>
              ))}
           </div>
        </div>

        {/* MENSAJES RECIENTES */}
        <div className={`rounded-xl shadow-sm border p-4 transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
           <div className="flex items-center justify-between mb-4 border-b pb-2">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Mensajes Recientes</label>
              <button 
                onClick={() => onNavigate?.(ViewState.COMMUNITY)}
                className="text-[9px] font-black uppercase tracking-widest text-indigo-500 hover:underline"
              >
                Ver todos
              </button>
           </div>
           
           {activeChats.length > 0 ? (
             <div className="space-y-2">
                {activeChats.slice(0, 3).map(chat => {
                   const otherId = chat.participants.find(p => p !== user?.uid);
                   const otherUser = directory.find(u => u.uid === otherId);
                   return (
                      <button 
                        key={chat.id}
                        onClick={() => handleOpenChat(chat)}
                        className={`w-full p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${isDarkMode ? 'bg-gray-800/50 border-gray-700 hover:bg-gray-800' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'}`}
                      >
                         <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center font-black text-[10px] ${isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'}`}>
                            {otherUser?.photoURL ? <img src={otherUser.photoURL} className="w-full h-full rounded-lg object-cover" alt="" /> : (otherUser?.displayName || 'U').substring(0,2).toUpperCase()}
                         </div>
                         <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-black uppercase tracking-tight truncate">{otherUser?.displayName || 'Usuario'}</p>
                            <p className="text-[10px] text-gray-500 truncate">{chat.lastMessage?.text || 'Sin mensajes'}</p>
                         </div>
                         <Clock size={12} className="text-gray-400" />
                      </button>
                   );
                })}
             </div>
           ) : (
             <div className="py-6 text-center opacity-30">
                <Mail size={24} className="mx-auto mb-2" />
                <p className="text-[9px] font-black uppercase tracking-widest">Sin actividad reciente</p>
             </div>
           )}
        </div>

        {/* 1. IDENTIDAD */}
        <div className={`rounded-xl shadow-sm border p-4 transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
           <label className="text-xs font-bold text-gray-400 uppercase mb-4 block tracking-wider border-b pb-2">1. Identidad y Contacto</label>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">Nombre Completo *</span>
                 <input type="text" name="name" value={formData.name} onChange={handleInputChange} disabled={isFieldDisabled} placeholder="Ej: Dr. Francisco Reyes" className={getInputClass(formData.name, true)} />
              </div>
              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1 flex items-center gap-1"><Mail size={10}/> Correo de Google</span>
                 <input type="text" value={formData.email} disabled className={`w-full px-3 py-2 border rounded-lg text-sm font-medium opacity-50 cursor-not-allowed ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-gray-100 border-gray-200 text-gray-500'}`} />
              </div>
              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">DNI / Pasaporte *</span>
                 <input type="text" name="dni" value={formData.dni} onChange={handleInputChange} disabled={isFieldDisabled} autoComplete="off" data-1p-ignore placeholder="Documento de Identidad" className={getInputClass(formData.dni, true)} />
              </div>
              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">País de Residencia</span>
                 <select name="country" value={formData.country} onChange={handleInputChange} disabled={isFieldDisabled} className={`w-full px-3 py-2 border rounded-lg text-sm font-medium ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                 </select>
              </div>
           </div>
        </div>

        {/* 2. ACREDITACIÓN PROFESIONAL */}
        <div className={`rounded-xl shadow-sm border p-4 transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
           <label className="text-xs font-bold text-gray-400 uppercase mb-4 block tracking-wider border-b pb-2">2. Acreditación Profesional e Identidad Digital</label>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">Estatus Profesional *</span>
                 <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} disabled={isFieldDisabled} className={`w-full px-3 py-2 border rounded-lg text-sm font-bold ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                    <option value="RADIOLOGIST">RADIÓLOGO</option>
                    <option value="RESIDENT">RESIDENTE</option>
                 </select>
              </div>

              {role === 'RESIDENT' ? (
                <div className="space-y-1 animate-in slide-in-from-top-2">
                   <span className="text-[10px] font-bold text-blue-500 uppercase ml-1">Año de Residencia *</span>
                   <select name="residentYear" value={formData.residentYear} onChange={handleInputChange} disabled={isFieldDisabled} className={`w-full px-3 py-2 border border-blue-100 rounded-lg text-sm font-black ${isDarkMode ? 'bg-blue-900/10 border-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-700'}`}>
                      {[1,2,3,4,5].map(y => <option key={y} value={y}>Año {y} (R{y})</option>)}
                   </select>
                </div>
              ) : (
                <div className="space-y-1 animate-in slide-in-from-top-2">
                   <span className="text-[10px] font-bold text-indigo-500 uppercase ml-1">Años de Trabajo como Radiólogo *</span>
                   <select name="experienceYears" value={formData.experienceYears} onChange={handleInputChange} disabled={isFieldDisabled} className={`w-full px-3 py-2 border border-indigo-100 rounded-lg text-sm font-black ${isDarkMode ? 'bg-indigo-900/10 border-indigo-900/30 text-indigo-400' : 'bg-indigo-50 text-indigo-700'}`}>
                      {EXP_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                   </select>
                </div>
              )}

              <div className="space-y-1 col-span-1 md:col-span-2">
                 <span className="text-[10px] font-bold text-blue-500 uppercase ml-1 flex items-center gap-1"><Linkedin size={10} /> Perfil de LinkedIn *</span>
                 <div className="relative">
                    <input type="url" name="linkedin" value={formData.linkedin} onChange={handleInputChange} disabled={isFieldDisabled} placeholder="https://www.linkedin.com/in/tu-perfil" className={`w-full px-3 py-2 border border-blue-100 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-blue-50/30 border-blue-200 text-gray-900'}`} />
                    {isVerified && <CheckCircle2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500" />}
                 </div>
              </div>

              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">Institución / Colegio *</span>
                 <input type="text" name="college" value={formData.college} onChange={handleInputChange} disabled={isFieldDisabled} placeholder="Ej: Colegio de Médicos de Madrid" className={getInputClass(formData.college, true)} />
              </div>
              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">Nº Colegiado / Registro *</span>
                 <input type="text" name="collegeNumber" value={formData.collegeNumber} onChange={handleInputChange} disabled={isFieldDisabled} placeholder="Nº Profesional" className={getInputClass(formData.collegeNumber, true)} />
              </div>
           </div>
        </div>

        {/* 3. ESPECIALIZACIÓN */}
        {role === 'RADIOLOGIST' && (
          <div className={`rounded-xl shadow-sm border p-4 transition-all animate-in slide-in-from-top-4 duration-300 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900'}`}>
             <div className="flex items-center space-x-2 mb-4 border-b pb-2">
                <GraduationCap size={18} className="text-blue-500" />
                <label className="text-xs font-bold text-gray-400 uppercase block tracking-wider">3. Especialización Radiológica</label>
             </div>
             
             <div className="space-y-6">
                <div>
                   <span className="text-[10px] font-bold text-gray-400 uppercase block mb-2 ml-1">Sub especialidad Radiológica (Principal) *</span>
                   <select 
                      name="mainSpecialty" 
                      value={formData.mainSpecialty} 
                      onChange={handleInputChange} 
                      disabled={isFieldDisabled} 
                      className={getInputClass(formData.mainSpecialty, true)}
                   >
                      <option value="">Selecciona tu área principal...</option>
                      {SPECIALTIES_LIST.map(spec => <option key={spec} value={spec}>{spec}</option>)}
                   </select>
                </div>

                <div>
                   <span className="text-[10px] font-bold text-gray-400 uppercase block mb-2 ml-1">Áreas de desempeño secundarias * (Máx 3)</span>
                   <div className={`flex flex-wrap gap-2 p-3 rounded-xl border border-dashed ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      {SPECIALTIES_LIST.map(spec => {
                         const active = selectedSpecialties.includes(spec);
                         const isMain = formData.mainSpecialty === spec;
                         if (isMain) return null;

                         return (
                            <button key={spec} type="button" onClick={() => toggleSpecialty(spec)} disabled={isFieldDisabled} className={`px-3 py-1.5 rounded-full border text-[10px] font-bold uppercase transition-all ${active ? 'bg-blue-600 border-blue-600 text-white shadow-sm' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-600')}`}>
                               {spec}
                            </button>
                         );
                      })}
                   </div>
                </div>
             </div>
          </div>
        )}

        {/* 4. UBICACIÓN Y TELERADIOLOGÍA */}
        <div className={`rounded-xl shadow-sm border p-4 transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
           <div className="flex items-center space-x-2 mb-4 border-b pb-2">
              <MapPin size={18} className="text-indigo-500" />
              <label className="text-xs font-bold text-gray-400 uppercase block tracking-wider">4. Entorno de Trabajo</label>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">Lugar Principal *</span>
                 <input type="text" name="workplace" value={formData.workplace} onChange={handleInputChange} disabled={isFieldDisabled || isLocationLocked} placeholder="Hospital o Centro" className={getInputClass(formData.workplace, true)} />
              </div>
              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">Ciudad *</span>
                 <input type="text" name="workCity" value={formData.workCity} onChange={handleInputChange} disabled={isFieldDisabled || isLocationLocked} placeholder="Ciudad" className={getInputClass(formData.workCity, true)} />
              </div>
              <div className="space-y-1">
                 <span className="text-[10px] font-bold text-gray-400 uppercase ml-1">País del Centro</span>
                 <select name="workCountry" value={formData.workCountry} onChange={handleInputChange} disabled={isFieldDisabled || isLocationLocked} className={`w-full px-3 py-2 border rounded-lg text-sm font-medium ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'} ${(isFieldDisabled || isLocationLocked) ? 'opacity-70 cursor-not-allowed' : ''}`}>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                 </select>
                 {isLocationLocked && (
                    <p className="flex items-start gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 mt-1.5 leading-tight">
                       <Lock size={12} className="text-amber-500 shrink-0 mt-0.5" />
                       <span>Tu centro de trabajo está verificado y bloqueado. Si te has trasladado, contacta al Master para actualizarlo.</span>
                    </p>
                 )}
              </div>
              <div className="flex items-end">
                 <button onClick={handleSearchWorkplace} disabled={isFieldDisabled || isLocationLocked || isSearchingLocation} className={`w-full px-4 py-2 rounded-lg border flex items-center justify-center space-x-2 text-[10px] font-black uppercase transition-all ${locationSuccess ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-blue-400' : 'bg-blue-50 border-blue-200 text-blue-600')} ${(isFieldDisabled || isLocationLocked) ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    {isSearchingLocation ? <RotateCw className="w-3 h-3 animate-spin" /> : locationSuccess ? <CheckCircle2 size={14} /> : <MapIcon size={14} />}
                    <span>{locationSuccess ? 'Centro Localizado' : 'Validar Ubicación'}</span>
                 </button>
              </div>
           </div>

           <div className={`p-4 rounded-xl border transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-indigo-50/30 border-indigo-100'}`}>
              <label className="flex items-center space-x-3 cursor-pointer">
                 <input type="checkbox" name="teleRadiologyInterest" checked={formData.teleRadiologyInterest} onChange={handleInputChange} disabled={isFieldDisabled} className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                 <div className="flex flex-col">
                    <span className={`text-xs font-black uppercase tracking-tight ${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'}`}>Interés en Tele-Radiología</span>
                    <span className="text-[9px] text-gray-400 font-bold uppercase">Deseo ser visible para oportunidades de colaboración remota.</span>
                 </div>
              </label>
           </div>
        </div>

        {/* 5. POLÍTICA Y TÉRMINOS */}
        <div className={`rounded-xl border p-5 space-y-4 ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-amber-50/20 border-amber-100'}`}>
           <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <ShieldCheck size={20} className="text-emerald-500 shrink-0 mt-0.5" />
                <div>
                   <h4 className="text-[10px] font-black uppercase text-emerald-600 tracking-widest mb-1">Protección de Datos Radiológicos</h4>
                   <p className="text-[9px] font-medium text-gray-500 leading-relaxed">Sus datos profesionales se almacenan en servidores cifrados bajo normativa RGPD. MODULAR no almacena información de pacientes. Sus datos de contacto solo serán visibles para colegas verificados de la red si usted así lo configura en su estatus de disponibilidad.</p>
                </div>
              </div>
              <button 
                onClick={() => setShowTermsModal(true)}
                className="p-2 rounded-lg hover:bg-white dark:hover:bg-gray-800 text-blue-500 transition-all flex items-center gap-1.5"
                title="Ver Términos y Condiciones"
              >
                <FileText size={16} />
                <span className="text-[9px] font-black uppercase">Leer</span>
              </button>
           </div>
           
           <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-800">
              <label className="flex items-start cursor-pointer">
                 <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} disabled={isFieldDisabled && termsAccepted} className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300" />
                 <div className="ml-3">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Acepto los términos de uso profesional y la política de privacidad.</span>
                 </div>
              </label>
              {termsAccepted && (
                <button 
                  onClick={() => setShowTermsModal(true)}
                  className="text-[9px] font-black text-blue-500 uppercase hover:underline"
                >
                  Renovar
                </button>
              )}
           </div>
        </div>

        <TermsModal 
          isOpen={showTermsModal}
          isDarkMode={isDarkMode}
          isSaving={isSaving}
          onClose={() => !isSaving && setShowTermsModal(false)}
          onAccept={handleAcceptTerms}
        />

        {/* Ajustes de Interfaz */}
        <div className={`mt-8 mb-6 p-5 rounded-[24px] border transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700 shadow-inner' : 'bg-white border-gray-100 shadow-sm'}`}>
          <label className="text-[10px] font-black text-gray-400 uppercase mb-4 block tracking-[0.2em]">Configuración de Interfaz</label>
          <div className={`flex items-center justify-between p-4 rounded-2xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-slate-50'} border ${isDarkMode ? 'border-gray-700' : 'border-slate-100'}`}>
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-indigo-500/10 rounded-xl text-indigo-500">
                <Activity size={20} />
              </div>
              <div>
                <p className="text-sm font-black uppercase tracking-tight leading-none mb-1">Burbujas de Notificación</p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Alertas de mensajes en tiempo real</p>
              </div>
            </div>
            <button 
              onClick={() => {
                const current = localStorage.getItem('modular_enable_notifications') !== 'false';
                localStorage.setItem('modular_enable_notifications', (!current).toString());
                window.dispatchEvent(new Event('app_settings_changed')); 
              }}
              className={`w-12 h-6 rounded-full relative transition-all duration-300 ${localStorage.getItem('modular_enable_notifications') !== 'false' ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]' : 'bg-gray-300 dark:bg-gray-700'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-md transition-all duration-300 ${localStorage.getItem('modular_enable_notifications') !== 'false' ? 'right-1' : 'left-1'}`} />
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 pt-2">

          {showIncompleteWarning && (
            <div className="w-full mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-start gap-3">
                <ShieldAlert className="text-amber-600 shrink-0 mt-0.5" size={20} />
                <div>
                  <h4 className="text-xs font-black uppercase text-amber-800 mb-1">Perfil Incompleto</h4>
                  <p className="text-[10px] font-medium text-amber-700 leading-relaxed">
                    Si continúas sin completar todos los campos obligatorios (*), tu acceso a las funciones avanzadas de MODULAR será limitado y tu perfil no será validado por la IA.
                  </p>
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button 
                  onClick={() => setShowIncompleteWarning(false)}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase text-amber-700 hover:bg-amber-100 transition-colors"
                >
                  Seguir Editando
                </button>
                <button 
                  onClick={handleSave}
                  className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all shadow-sm"
                >
                  Entrar con limitaciones
                </button>
              </div>
            </div>
          )}

          {isEditing ? (
            <button 
              onClick={handleSave} 
              disabled={isSaving || !termsAccepted}
              className={`flex-1 flex items-center justify-center space-x-2 py-4 rounded-xl font-bold text-sm shadow-md transition-all active:scale-[0.98] uppercase tracking-wider ${isSaving || !termsAccepted ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
            >
               {isSaving ? (
                  <div className="flex items-center space-x-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div><span>GUARDANDO...</span></div>
               ) : (
                 <><Save size={18} /><span>{isProfileIncomplete() ? 'Continuar con limitaciones' : 'Finalizar Registro'}</span></>
               )}
            </button>
          ) : (
              <button 
                onClick={() => setIsEditing(true)} 
                className={`flex-1 px-6 py-4 rounded-xl border font-bold text-xs uppercase transition-all flex items-center justify-center space-x-2 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-amber-500' : 'bg-white border-gray-200 text-gray-400 hover:text-amber-600'}`}
              >
                 <Pencil size={16} />
                 <span>MODIFICAR REGISTRO</span>
              </button>
           )}
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
